import { type ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import { Music } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { PieceRow } from '@/components/molecules/piece-row';
import { masteryVisualLevel } from '@/lib/mastery';
import type { RepertoireEntry } from '@/lib/types';

type NavigateMode = {
  mode: 'navigate';
};

type SelectMode = {
  mode: 'select';
  selectedArrangementId: string | null;
  onSelect: (arrangementId: string) => void;
};

export type RepertoireListDensity = 'responsive' | 'single-column';

type RepertoireListBaseProps = {
  entries: RepertoireEntry[];
  masterySortOrderById: Map<string, number>;
  density?: RepertoireListDensity;
};

export type RepertoireListProps = RepertoireListBaseProps & (NavigateMode | SelectMode);

const GRID_BY_DENSITY: Record<RepertoireListDensity, string> = {
  'responsive':    'grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3 list-none p-0 m-0',
  'single-column': 'grid grid-cols-1 gap-2 list-none p-0 m-0',
};

export function RepertoireList(props: RepertoireListProps): ReactElement {
  const { entries, masterySortOrderById, density = 'responsive' } = props;

  if (entries.length === 0) {
    return <RepertoireListEmpty />;
  }

  return (
    <ul data-repertoire-list className={GRID_BY_DENSITY[density]}>
      {entries.map((entry) => {
        const masterySortOrder = entry.masteryLevelId
          ? masterySortOrderById.get(entry.masteryLevelId) ?? null
          : null;
        const visualLevel = masteryVisualLevel(masterySortOrder);
        const isActive = props.mode === 'select' && props.selectedArrangementId === entry.arrangementId;

        const row = (
          <PieceRow
            title={entry.pieceName}
            composer="Traditional"
            form={entry.formName ?? '—'}
            musicalKey={entry.keyName ?? '—'}
            mastery={visualLevel}
            dueNow={false}
            active={isActive}
            tags={[]}
            compact={true}
          />
        );

        if (props.mode === 'navigate') {
          return (
            <li key={entry.userArrangementId}>
              <Link
                data-repertoire-row
                to="/repertoire/$arrangementId"
                params={{ arrangementId: entry.arrangementId }}
                className="block rounded-[var(--r-md)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]"
              >
                {row}
              </Link>
            </li>
          );
        }

        return (
          <li key={entry.userArrangementId}>
            <button
              type="button"
              data-repertoire-row
              data-active={isActive ? 'true' : 'false'}
              onClick={() => props.onSelect(entry.arrangementId)}
              className="block w-full text-left rounded-[var(--r-md)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]"
              aria-current={isActive ? 'true' : undefined}
            >
              {row}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function RepertoireListEmpty(): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center rounded-[var(--r-lg)] border border-[var(--rule)] bg-[var(--bg-surface)] py-16 text-center">
      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[var(--bg-soft)] text-[var(--ink-3)]">
        <Music className="h-9 w-9 opacity-60" aria-hidden />
      </div>
      <h3 className="font-display mt-6 text-xl font-semibold text-[var(--ink)]">
        Start building your repertoire
      </h3>
      <p className="mt-2 max-w-[300px] text-sm text-[var(--ink-3)]">
        Your personal library is empty. Add your first piece to start tracking daily progress and mastery.
      </p>
      <div className="mt-6">
        <Link to="/library">
          <Button size="lg">Add first piece</Button>
        </Link>
      </div>
    </div>
  );
}
